<template>
  <div>
    <!-- Фильтры -->
    <div class="tw-flex tw-items-end tw-gap-4 tw-mb-6 tw-flex-wrap">
      <CustomSelect
        :options="versions"
        label="Версия сметы 1"
        option-label-key="version_label"
        option-value-key="smeta_version_id"
        @change="onVersion1Change"
        :value="selectedVersion1Id"
        placeholder="Выберите первую версию"
        :clearable="false"
        style="width: 280px;"
      />
      <CustomSelect
        :options="versions"
        label="Версия сметы 2"
        option-label-key="version_label"
        option-value-key="smeta_version_id"
        @change="onVersion2Change"
        :value="selectedVersion2Id"
        placeholder="Выберите вторую версию"
        :clearable="false"
        style="width: 280px;"
      />
      <CustomSelect
        :options="entranceOptions"
        label="Подъезд"
        option-label-key="label"
        option-value-key="project_entrance_id"
        @change="onEntranceChange"
        :value="selectedEntranceId"
        placeholder="Все подъезды"
        :clearable="true"
      />
      <CustomSelect
        :options="floorOptions"
        label="Этаж"
        option-label-key="label"
        option-value-key="floor_map_id"
        @change="onFloorChange"
        :value="selectedFloorMapId"
        placeholder="Все этажи"
        :clearable="true"
        :disable="!selectedEntranceId"
      />
      <q-btn
        :disable="!selectedVersion1Id || !selectedVersion2Id || isComparing"
        color="primary"
        label="Сравнить"
        @click="compareVersions"
        :loading="isComparing"
        class="tw-h-[40px] tw-text-white tw-rounded-md tw-capitalize submit-btn"
      />
    </div>

    <!-- Нет выбранных версий -->
    <div
      v-if="!selectedVersion1Id || !selectedVersion2Id"
      style="padding: 40px; background: #f5f5f5; border-radius: 8px; text-align: center;"
    >
      <q-icon name="info" size="48px" color="grey-5" />
      <p style="color: #666; margin: 16px 0 0 0;">Выберите две версии сметы для сравнения</p>
    </div>

    <!-- Загрузка -->
    <div v-else-if="isComparing" style="padding: 40px; text-align: center;">
      <q-spinner color="primary" size="3em" />
      <p style="color: #666; margin-top: 16px;">Загрузка сравнения...</p>
    </div>

    <!-- Данные -->
    <div v-else-if="hasCompared">

      <!-- Легенда версий + итого в одной строке -->
      <div class="version-legend">
        <div class="version-legend__versions">
          <div class="version-badge v1-badge">
            <div class="ver-main">
              <span class="dot v1-dot"></span>
              <span>V{{ version1Info?.version_no }} — {{ version1Info?.reason_name || 'без причины' }}</span>
              <span class="ver-date">{{ version1Info?.created_at }}</span>
            </div>
            <div v-if="version1Info?.fio" class="ver-fio">{{ version1Info.fio }}</div>
          </div>
          <div class="version-legend__arrow">↔</div>
          <div class="version-badge v2-badge">
            <div class="ver-main">
              <span class="dot v2-dot"></span>
              <span>V{{ version2Info?.version_no }} — {{ version2Info?.reason_name || 'без причины' }}</span>
              <span class="ver-date">{{ version2Info?.created_at }}</span>
            </div>
            <div v-if="version2Info?.fio" class="ver-fio">{{ version2Info.fio }}</div>
          </div>
        </div>
        <div v-if="data.length" class="version-legend__totals">
          <div class="total-item">
            <span class="total-label">Итого V{{ version1Info?.version_no }}</span>
            <span class="total-value v1-color"><Amount :value="grandTotalV1" :round="2" /> тг</span>
          </div>
          <div class="total-item">
            <span class="total-label">Итого V{{ version2Info?.version_no }}</span>
            <span class="total-value v2-color"><Amount :value="grandTotalV2" :round="2" /> тг</span>
          </div>
          <div class="total-item">
            <span class="total-label">Разница</span>
            <span class="total-value" :class="grandTotalV2 > grandTotalV1 ? 'diff-up' : grandTotalV2 < grandTotalV1 ? 'diff-down' : ''">
              <q-icon :name="diffIcon(grandTotalV1, grandTotalV2)" size="18px" />
              <Amount :value="Math.abs(grandTotalV2 - grandTotalV1)" :round="2" /> тг
            </span>
          </div>
        </div>
      </div>

      <!-- Переключатели -->
      <div style="display: flex; justify-content: flex-end; gap: 24px; margin-bottom: 12px; flex-wrap: wrap;">
        <q-toggle v-model="showOnlyDifferences" label="Показать только изменения" dense />
        <q-toggle v-model="showOnlyVolumeChanges" label="Только изменения в объёмах" dense />
      </div>

      <!-- Нет данных -->
      <div
        v-if="!filteredData.length"
        style="padding: 40px; background: #f5f5f5; border-radius: 8px; text-align: center;"
      >
        <q-icon name="check_circle" size="48px" color="green" />
        <p style="color: #666; margin: 16px 0 0 0;">
          {{
            showOnlyVolumeChanges
              ? 'Нет изменений в объёмах между выбранными версиями'
              : showOnlyDifferences
              ? 'Нет различий между выбранными версиями'
              : 'Нет данных'
          }}
        </p>
      </div>

      <!-- Таблица -->
      <CustomTable
        v-else
        :rowsData="filteredData"
        :customHeader="true"
        :custom-body="true"
        :small="true"
      >
        <template #customHeader>
          <!-- Строка 1 -->
          <q-tr>
            <q-th rowspan="2" align="center" style="min-width:70px; font-weight:700;">Подъезд /<br>Этаж</q-th>
            <q-th rowspan="2" align="left" style="min-width:200px; font-weight:700;">Конструктив</q-th>
            <q-th colspan="2" align="center" style="font-weight:700; border-left:2px solid #90caf9;">СМР</q-th>
            <q-th colspan="4" align="center" style="font-weight:700; border-left:2px solid #a5d6a7;">Материал</q-th>
            <q-th colspan="2" align="center" style="font-weight:700; border-left:2px solid #ffcc80;">Итого, тг</q-th>
          </q-tr>
          <!-- Строка 2 -->
          <q-tr class="sticky innerTableHeader">
            <q-th align="right" style="min-width:90px; border-left:2px solid #90caf9;">Кол-во</q-th>
            <q-th align="right" style="min-width:110px;">Сумма, тг</q-th>
            <q-th align="left" style="min-width:180px; border-left:2px solid #a5d6a7;">Наименование</q-th>
            <q-th align="left" style="min-width:130px;">Тип закупа</q-th>
            <q-th align="right" style="min-width:90px;">Кол-во</q-th>
            <q-th align="right" style="min-width:110px;">Сумма, тг</q-th>
            <q-th align="right" style="min-width:130px; border-left:2px solid #ffcc80;">
              V{{ version1Info?.version_no }} / V{{ version2Info?.version_no }}
            </q-th>
            <q-th align="center" style="min-width:90px;">Δ</q-th>
          </q-tr>
        </template>

        <template #customBody="props">
          <!-- Заголовок группы -->
          <template v-if="props.row.isGroupHeader">
            <q-tr class="group-header-row">
              <q-td colspan="10" class="group-header-cell">
                {{ props.row.work_set_check_group_name || 'Без группы' }}
              </q-td>
            </q-tr>
          </template>

          <!-- Строка данных -->
          <q-tr v-else :style="rowBg(props.row)">

            <!-- Подъезд / Этаж -->
            <custom-table-cell
              v-if="props.row.work_rowspan"
              :rowspan="props.row.work_rowspan"
              align="center"
              vertical-align="center"
            >
              <div class="entrance-cell">
                <span class="entrance-label">П.{{ props.row.entrance }}</span>
                <span class="floor-label">Эт.{{ props.row.level_num }}</span>
              </div>
            </custom-table-cell>

            <!-- Конструктив -->
            <custom-table-cell
              v-if="props.row.work_rowspan"
              :rowspan="props.row.work_rowspan"
              align="left"
              vertical-align="center"
              style="border-left:2px solid #e3f2fd;"
            >
              <div style="font-size:12px; line-height:1.4;">
                <span :class="{ 'work-diff': isWorkDiff(props.row) }">{{ props.row.wname }}</span>
                <span v-if="props.row.wunt" class="unit-text"> ({{ props.row.wunt }})</span>
                <span v-if="props.row.work_rowspan > 1" class="multi-badge">{{ props.row.work_rowspan }} мат.</span>
              </div>
            </custom-table-cell>

            <!-- СМР: Кол-во (V1/V2 стопкой) -->
            <custom-table-cell
              v-if="props.row.work_rowspan"
              :rowspan="props.row.work_rowspan"
              align="right"
              vertical-align="center"
              style="border-left:2px solid #e3f2fd;"
            >
              <div class="val-stack">
                <span class="v1-val" :class="{ 'changed': isWorkDiff(props.row) }">{{ fmtQty(props.row.wqty_v1) }}</span>
                <span class="v2-val" :class="{ 'changed': isWorkDiff(props.row) }">{{ fmtQty(props.row.wqty_v2) }}</span>
              </div>
            </custom-table-cell>

            <!-- СМР: Сумма (V1/V2 стопкой) -->
            <custom-table-cell
              v-if="props.row.work_rowspan"
              :rowspan="props.row.work_rowspan"
              align="right"
              vertical-align="center"
            >
              <div class="val-stack">
                <span class="v1-val"><Amount :value="props.row.wsum_v1" :round="2" /></span>
                <span class="v2-val"><Amount :value="props.row.wsum_v2" :round="2" /></span>
              </div>
            </custom-table-cell>

            <!-- Материал: Наименование -->
            <custom-table-cell
              align="left"
              vertical-align="center"
              style="border-left:2px solid #e8f5e9;"
            >
              <span style="font-size:12px;" :class="{ 'mat-diff': isMaterialDiff(props.row) }">
                {{ props.row.mname || '—' }}
                <span v-if="props.row.munt" class="unit-text"> ({{ props.row.munt }})</span>
              </span>
            </custom-table-cell>

            <!-- Материал: тип закупа (V1/V2 стопкой) -->
            <custom-table-cell align="left" vertical-align="center">
              <div class="val-stack" v-if="props.row.mname">
                <span class="v1-val purchase-type-val" :class="{ 'changed': isPurchaseTypeDiff(props.row) }">
                  {{ fmtPurchaseType(props.row.purchase_type_id_v1) }}
                </span>
                <span class="v2-val purchase-type-val" :class="{ 'changed': isPurchaseTypeDiff(props.row) }">
                  {{ fmtPurchaseType(props.row.purchase_type_id_v2) }}
                </span>
              </div>
              <span v-else class="empty-val">—</span>
            </custom-table-cell>

            <!-- Материал: Кол-во (V1/V2 стопкой) -->
            <custom-table-cell align="right" vertical-align="center">
              <div class="val-stack" v-if="props.row.mname">
                <span class="v1-val" :class="{ 'changed': isMaterialDiff(props.row) }">{{ fmtQty(props.row.mqty_v1) }}</span>
                <span class="v2-val" :class="{ 'changed': isMaterialDiff(props.row) }">{{ fmtQty(props.row.mqty_v2) }}</span>
              </div>
              <span v-else class="empty-val">—</span>
            </custom-table-cell>

            <!-- Материал: Сумма (V1/V2 стопкой) -->
            <custom-table-cell align="right" vertical-align="center">
              <div class="val-stack" v-if="props.row.mname">
                <span class="v1-val"><Amount :value="props.row.msum_v1" :round="2" /></span>
                <span class="v2-val"><Amount :value="props.row.msum_v2" :round="2" /></span>
              </div>
              <span v-else class="empty-val">—</span>
            </custom-table-cell>

            <!-- Итого V1/V2 стопкой -->
            <custom-table-cell
              v-if="props.row.work_rowspan"
              :rowspan="props.row.work_rowspan"
              align="right"
              vertical-align="center"
              style="border-left:2px solid #fff3e0;"
            >
              <div class="val-stack">
                <span class="v1-val ttl-val"><Amount :value="props.row.ttl_v1" :round="2" /></span>
                <span class="v2-val ttl-val"><Amount :value="props.row.ttl_v2" :round="2" /></span>
              </div>
            </custom-table-cell>

            <!-- Δ (итого, только первая строка) -->
            <custom-table-cell
              v-if="props.row.work_rowspan"
              :rowspan="props.row.work_rowspan"
              align="center"
              vertical-align="center"
            >
              <div v-if="props.row.ttl_v1 !== props.row.ttl_v2" class="delta-cell">
                <q-icon
                  :name="diffIcon(props.row.ttl_v1, props.row.ttl_v2)"
                  :color="diffColor(props.row.ttl_v1, props.row.ttl_v2)"
                  size="16px"
                />
                <span :class="props.row.ttl_v2 > props.row.ttl_v1 ? 'diff-up' : 'diff-down'">
                  <Amount :value="Math.abs((props.row.ttl_v2 || 0) - (props.row.ttl_v1 || 0))" :round="2" />
                </span>
              </div>
              <span v-else class="empty-val">=</span>
            </custom-table-cell>

          </q-tr>
        </template>
      </CustomTable>
    </div>
  </div>
</template>

<script>
import {
  getSmetaVersions,
  getProjectEntrances,
  getProjectEntranceFloors,
  getSmetaCompareNewReport,
} from './services';
import Amount from '@/components/common/Amount.vue';
import CustomSelect from '@/components/form/CustomSelect';
import CustomTable from '@/components/common/CustomTable';
import CustomTableCell from '@/components/common/CustomTable/custom-table-cell';

export default {
  name: 'ProjectSmetaCompareNew',
  components: { Amount, CustomSelect, CustomTable, CustomTableCell },
  props: {
    projectId: { type: Number, required: true },
  },
  data() {
    return {
      versions: [],
      selectedVersion1Id: null,
      selectedVersion2Id: null,
      isComparing: false,
      data: [],
      hasCompared: false,
      showOnlyDifferences: false,
      showOnlyVolumeChanges: false,
      entranceOptions: [],
      floorOptions: [],
      selectedEntranceId: null,
      selectedFloorMapId: null,
    };
  },
  computed: {
    version1Info() {
      return this.versions.find((v) => v.smeta_version_id === this.selectedVersion1Id);
    },
    version2Info() {
      return this.versions.find((v) => v.smeta_version_id === this.selectedVersion2Id);
    },
    // Ключи work-групп, в которых есть хоть одно отличие (работа или материал — любое)
    diffGroupKeys() {
      return this._buildGroupKeys((row) => this.isWorkDiff(row) || this.isMaterialDiff(row));
    },
    // Ключи work-групп, где изменился объём (qty) работы или материала
    volDiffGroupKeys() {
      return this._buildGroupKeys((row) => this.isWorkVolumeDiff(row) || this.isMaterialVolumeDiff(row));
    },
    // Ключи заголовков групп (group header) с отличиями
    diffGroupHeaderKeys() {
      return this._buildHeaderKeys(this.diffGroupKeys);
    },
    volDiffGroupHeaderKeys() {
      return this._buildHeaderKeys(this.volDiffGroupKeys);
    },
    filteredData() {
      let dk, dhk;
      if (this.showOnlyVolumeChanges) {
        dk = this.volDiffGroupKeys;
        dhk = this.volDiffGroupHeaderKeys;
      } else if (this.showOnlyDifferences) {
        dk = this.diffGroupKeys;
        dhk = this.diffGroupHeaderKeys;
      } else {
        return this.data;
      }
      return this.data.filter((row) => {
        if (row.isGroupHeader) {
          return dhk.has(`${row.entrance || ''}_${row.level_num ?? ''}_${row.work_set_check_group_id ?? ''}`);
        }
        return dk.has(row.groupKey);
      });
    },
    grandTotalV1() {
      return this.data
        .filter((r) => !r.isGroupHeader && r.work_rowspan > 0)
        .reduce((s, r) => s + (r.ttl_v1 || 0), 0);
    },
    grandTotalV2() {
      return this.data
        .filter((r) => !r.isGroupHeader && r.work_rowspan > 0)
        .reduce((s, r) => s + (r.ttl_v2 || 0), 0);
    },
  },
  methods: {
    // Любое отличие (объём или сумма)
    isWorkDiff(row) {
      return (
        Number(row.wqty_v1 || 0) !== Number(row.wqty_v2 || 0) ||
        Number(row.wsum_v1 || 0) !== Number(row.wsum_v2 || 0)
      );
    },
    isMaterialDiff(row) {
      if (!row.mname) return false;
      return (
        Number(row.mqty_v1 || 0) !== Number(row.mqty_v2 || 0) ||
        Number(row.msum_v1 || 0) !== Number(row.msum_v2 || 0) ||
        this.isPurchaseTypeDiff(row)
      );
    },
    isPurchaseTypeDiff(row) {
      if (!row.mname) return false;
      const a = row.purchase_type_id_v1 != null && row.purchase_type_id_v1 !== '' ? Number(row.purchase_type_id_v1) : null;
      const b = row.purchase_type_id_v2 != null && row.purchase_type_id_v2 !== '' ? Number(row.purchase_type_id_v2) : null;
      return a !== b;
    },
    // Только изменение объёма (qty)
    isWorkVolumeDiff(row) {
      return Number(row.wqty_v1 || 0) !== Number(row.wqty_v2 || 0);
    },
    isMaterialVolumeDiff(row) {
      if (!row.mname) return false;
      return Number(row.mqty_v1 || 0) !== Number(row.mqty_v2 || 0);
    },
    // Строит Set ключей work-групп, удовлетворяющих предикату
    _buildGroupKeys(predicate) {
      const keys = new Set();
      this.data.forEach((row) => {
        if (row.isGroupHeader) return;
        if (predicate(row)) keys.add(row.groupKey);
      });
      return keys;
    },
    // Строит Set ключей заголовков групп по набору ключей work-групп
    _buildHeaderKeys(groupKeys) {
      const keys = new Set();
      this.data.forEach((row) => {
        if (row.isGroupHeader) return;
        if (groupKeys.has(row.groupKey)) {
          keys.add(`${row.entrance || ''}_${row.level_num ?? ''}_${row.work_set_check_group_id ?? ''}`);
        }
      });
      return keys;
    },
    rowBg(row) {
      if (this.isWorkDiff(row) || this.isMaterialDiff(row)) return 'background:#fff8e1;';
      return '';
    },
    diffIcon(v1, v2) {
      const d = (v2 || 0) - (v1 || 0);
      if (d > 0) return 'arrow_upward';
      if (d < 0) return 'arrow_downward';
      return 'remove';
    },
    diffColor(v1, v2) {
      const d = (v2 || 0) - (v1 || 0);
      if (d > 0) return 'red';
      if (d < 0) return 'green';
      return 'grey';
    },
    fmtQty(val) {
      if (val == null || Number(val) === 0) return '—';
      return Number(val).toLocaleString('ru-RU', { maximumFractionDigits: 2 });
    },
    fmtPurchaseType(id) {
      if (id == null || id === '') return '—';
      const n = Number(id);
      if (n === 1) return 'Подряд';
      if (n === 2) return 'Команда проекта';
      return String(id);
    },
    async loadVersions() {
      const res = await getSmetaVersions(this.projectId);
      if (res && res.length > 0) {
        this.versions = res.map((v) => ({
          ...v,
          version_label: `v${v.version_no} — ${v.reason_name || 'без причины'} (${v.created_at})`,
        }));
        if (this.versions.length >= 2) {
          this.selectedVersion1Id = this.versions[1].smeta_version_id;
          this.selectedVersion2Id = this.versions[0].smeta_version_id;
        }
      }
    },
    async loadEntrances() {
      const res = await getProjectEntrances(this.projectId);
      this.entranceOptions = res || [];
    },
    onVersion1Change(value) {
      this.selectedVersion1Id = value;
      this.data = [];
      this.hasCompared = false;
    },
    onVersion2Change(value) {
      this.selectedVersion2Id = value;
      this.data = [];
      this.hasCompared = false;
    },
    async onEntranceChange(value) {
      this.selectedEntranceId = value || null;
      this.selectedFloorMapId = null;
      this.floorOptions = [];
      this.data = [];
      this.hasCompared = false;
      if (this.selectedEntranceId) {
        const entrance = this.entranceOptions.find(
          (e) => e.project_entrance_id === this.selectedEntranceId
        );
        if (entrance) {
          const floors = await getProjectEntranceFloors(this.projectId, entrance.entrance);
          this.floorOptions = floors || [];
        }
      }
    },
    async onFloorChange(value) {
      this.selectedFloorMapId = value || null;
      this.data = [];
      this.hasCompared = false;
    },
    async compareVersions() {
      if (!this.selectedVersion1Id || !this.selectedVersion2Id) return;
      this.isComparing = true;
      this.data = [];
      const res = await getSmetaCompareNewReport(
        this.projectId,
        this.selectedVersion1Id,
        this.selectedVersion2Id,
        this.selectedEntranceId,
        this.selectedFloorMapId
      );
      this.data = res || [];
      this.hasCompared = true;
      this.isComparing = false;
    },
  },
  async mounted() {
    await Promise.all([this.loadVersions(), this.loadEntrances()]);
  },
};
</script>

<style scoped lang="scss">
/* Легенда версий + итого в одной полосе */
.version-legend {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px 24px;
  flex-wrap: wrap;
  margin-bottom: 12px;
  padding: 10px 16px;
  background: #e3f2fd;
  border-radius: 8px;
  font-size: 13px;
}
.version-legend__versions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  min-width: 0;
}
.version-legend__arrow {
  font-size: 20px;
  color: #aaa;
  flex-shrink: 0;
}
.version-legend__totals {
  display: flex;
  align-items: center;
  gap: 24px 32px;
  flex-wrap: wrap;
  margin-left: auto;
}
.version-badge {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-weight: 600;
}
.ver-main {
  display: flex;
  align-items: center;
  gap: 6px;
}
.dot {
  width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0;
}
.v1-dot { background: #e65100; }
.v2-dot { background: #1565c0; }
.ver-date { font-weight: 400; color: #666; font-size: 12px; }
.ver-fio  { font-weight: 400; color: #555; font-size: 12px; font-style: italic; margin-left: 16px; }

.total-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.total-label { font-size: 11px; color: #888; }
.total-value {
  font-weight: 700;
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Цвета */
.v1-color { color: #e65100; }
.v2-color { color: #1565c0; }
.diff-up   { color: #d32f2f; }
.diff-down { color: #388e3c; }

/* Ячейка подъезда */
.entrance-cell {
  display: flex; flex-direction: column; align-items: center; gap: 2px;
  .entrance-label { font-weight: 600; font-size: 12px; }
  .floor-label    { font-size: 11px; color: #666; }
}

/* Конструктив */
.unit-text  { color: #888; font-style: italic; }
.multi-badge { margin-left: 6px; font-size: 10px; color: #1976d2; background: #e3f2fd; padding: 1px 5px; border-radius: 10px; }
.work-diff  { color: #e65100; font-weight: 600; }
.mat-diff   { color: #e65100; font-weight: 600; }

/* Стопка V1/V2 */
.val-stack {
  display: flex;
  flex-direction: column;
  gap: 3px;
  line-height: 1.3;
}
.v1-val {
  font-size: 12px;
  color: #e65100;
  &.changed { font-weight: 700; }
}
.v2-val {
  font-size: 12px;
  color: #1565c0;
  &.changed { font-weight: 700; }
}
.ttl-val { font-weight: 600; font-size: 13px; }
.purchase-type-val { font-size: 11px; white-space: nowrap; }
.empty-val { color: #ccc; font-size: 12px; }

/* Дельта */
.delta-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 600;
}

/* Заголовок группы */
.group-header-row { background: #f0f4ff; }
.group-header-cell {
  font-weight: 700;
  font-size: 13px;
  padding: 8px 12px !important;
  color: #1a237e;
  border-top: 2px solid #c5cae9;
}
</style>
